import { useState, useEffect } from 'react';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { Modal } from '../../components/common/Modal';
import { Input } from '../../components/common/Input';
import { deviceService } from '../../services/device';
import type { Device, DeviceToken, ChannelConfig } from '../../types/device';

export function DevicesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isBindModalOpen, setIsBindModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [deviceTokens, setDeviceTokens] = useState<DeviceToken[]>([]);
  const [channelConfigs, setChannelConfigs] = useState<ChannelConfig[]>([]);
  const pageSize = 10;

  const [bindForm, setBindForm] = useState({
    device_id: '',
    device_secret: '',
  });

  const fetchDevices = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await deviceService.listDevices(page, pageSize);
      setDevices(response.data);
      setTotal(response.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [page]);

  const handleBind = async () => {
    try {
      await deviceService.bindDevice(bindForm);
      setIsBindModalOpen(false);
      setBindForm({ device_id: '', device_secret: '' });
      fetchDevices();
    } catch (err: any) {
      alert(err.response?.data?.error?.message || '绑定失败');
    }
  };

  const handleUnbind = async (id: number) => {
    if (!confirm('确定要解绑此设备吗？')) {
      return;
    }

    try {
      await deviceService.unbindDevice(id);
      fetchDevices();
    } catch (err: any) {
      alert(err.response?.data?.error?.message || '解绑失败');
    }
  };

  const handleViewDetail = async (device: Device) => {
    setSelectedDevice(device);
    setIsDetailModalOpen(true);

    try {
      const [tokens, configs] = await Promise.all([
        deviceService.listTokens(device.id),
        deviceService.listChannelConfigs(device.id),
      ]);
      setDeviceTokens(tokens);
      setChannelConfigs(configs);
    } catch (err: any) {
      console.error('Failed to fetch device details:', err);
    }
  };

  const handleRegenerateToken = async () => {
    if (!selectedDevice) return;

    try {
      const response = await deviceService.regenerateToken(selectedDevice.id);
      alert(`新 Token 已生成: ${response.token}\n请保存此 Token，它只会显示一次。`);
      const tokens = await deviceService.listTokens(selectedDevice.id);
      setDeviceTokens(tokens);
    } catch (err: any) {
      alert(err.response?.data?.error?.message || '生成失败');
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('zh-CN');
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      deleted: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      active: '活跃',
      inactive: '未激活',
      suspended: '已暂停',
      deleted: '已删除',
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.inactive}`}>
        {labels[status] || status}
      </span>
    );
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">设备管理</h1>
          <p className="text-sm text-gray-600 mt-1">管理您的 PicoClaw 设备</p>
        </div>
        <Button onClick={() => setIsBindModalOpen(true)}>
          绑定设备
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <Card>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            暂无设备，点击上方按钮绑定新设备
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">设备ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">名称</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">型号</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">状态</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">最后在线</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">创建时间</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr key={device.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-mono">{device.device_id}</td>
                    <td className="py-3 px-4 text-sm">{device.name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{device.model}</td>
                    <td className="py-3 px-4">{getStatusBadge(device.status)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDate(device.last_online_at)}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{formatDate(device.created_at)}</td>
                    <td className="py-3 px-4 text-right space-x-2">
                      <button
                        onClick={() => handleViewDetail(device)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        详情
                      </button>
                      <button
                        onClick={() => handleUnbind(device.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        解绑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              共 {total} 条记录
            </div>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                上一页
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                下一页
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Modal
        isOpen={isBindModalOpen}
        onClose={() => setIsBindModalOpen(false)}
        title="绑定设备"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">设备ID</label>
            <Input
              value={bindForm.device_id}
              onChange={(e) => setBindForm({ ...bindForm, device_id: e.target.value })}
              placeholder="LICHEE-XXXX-XXXX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">设备密钥</label>
            <Input
              type="password"
              value={bindForm.device_secret}
              onChange={(e) => setBindForm({ ...bindForm, device_secret: e.target.value })}
              placeholder="输入设备密钥"
            />
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="secondary" onClick={() => setIsBindModalOpen(false)}>
              取消
            </Button>
            <Button onClick={handleBind}>
              绑定
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={selectedDevice ? `设备详情 - ${selectedDevice.device_id}` : '设备详情'}
      >
        {selectedDevice && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">基本信息</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">设备ID:</span>
                  <span className="ml-2 font-mono">{selectedDevice.device_id}</span>
                </div>
                <div>
                  <span className="text-gray-500">名称:</span>
                  <span className="ml-2">{selectedDevice.name || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">型号:</span>
                  <span className="ml-2">{selectedDevice.model}</span>
                </div>
                <div>
                  <span className="text-gray-500">固件版本:</span>
                  <span className="ml-2">{selectedDevice.firmware_version || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">状态:</span>
                  <span className="ml-2">{getStatusBadge(selectedDevice.status)}</span>
                </div>
                <div>
                  <span className="text-gray-500">IP地址:</span>
                  <span className="ml-2">{selectedDevice.ip_address || '-'}</span>
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium text-gray-900">访问令牌</h3>
                <Button size="sm" onClick={handleRegenerateToken}>
                  重新生成
                </Button>
              </div>
              {deviceTokens.length === 0 ? (
                <p className="text-sm text-gray-500">暂无令牌</p>
              ) : (
                <div className="space-y-2">
                  {deviceTokens.map((token) => (
                    <div key={token.id} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                      <span className="font-mono">{token.token_prefix}...</span>
                      <span className="text-gray-500">
                        过期: {formatDate(token.expires_at)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">渠道配置</h3>
              {channelConfigs.length === 0 ? (
                <p className="text-sm text-gray-500">暂无渠道配置</p>
              ) : (
                <div className="space-y-2">
                  {channelConfigs.map((config) => (
                    <div key={config.id} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                      <span className="font-medium">{config.channel_type}</span>
                      <span className={config.enabled ? 'text-green-600' : 'text-gray-500'}>
                        {config.enabled ? '已启用' : '已禁用'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={() => setIsDetailModalOpen(false)}>
                关闭
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
